import { SOCKET_CREDENTIAL_KIND_REFUSED_CLOSE_CODE } from '@featit/shared/as-if-routes';
import type { FormattedExecutionResult, Sink } from 'graphql-ws';
import { workAreaDataSourceRegistry } from '@/data-sources/WorkAreaDataSourceRegistry';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import {
  getDesktopCapabilities,
  subscribeDesktopCloudRealtime,
} from '@/desktop/desktopRuntimeLazy';
import type { DesktopRealtimeMessageType } from '@/desktop/desktopRuntime';

/**
 * Realtime per connection (T-0549, `docs/concept-generic-view-engine.md` FR-4, RTC-1 to RTC-7).
 *
 * A work area's subscriptions go to the deployment that holds it, the way its requests and uploads
 * do (I-1120, I-1140). The main connection is the tab's one graphql-ws socket (`WorkItemApi.ts`,
 * lazy-loaded). A work area bound to a custom connection on the desktop (ADR-153) subscribes through
 * the desktop's relay (`cloud_realtime_subscribe`), which holds that connection's credential: the
 * token never reaches the webview. Every work area on one connection shares one client here, and
 * one socket in the relay.
 *
 * Each connection's state is its own. A custom connection that drops tells only its own listeners;
 * the main connection's views stay live, and the other way round.
 */

export interface IRealtimeSubscriber {
  subscribe<T>(
    payload: { query: string; variables?: Record<string, unknown> | null },
    sink: Sink<FormattedExecutionResult<T>>,
  ): () => void;
}

/** Names a connection's socket: `main`, or `connection:<id>` for a custom connection. */
export type RealtimeConnectionKeyType = string;

export const MAIN_REALTIME_CONNECTION: RealtimeConnectionKeyType = 'main';

type ConnectionListenerType = (connected: boolean, connection: RealtimeConnectionKeyType) => void;

const connectedByKey = new Map<RealtimeConnectionKeyType, boolean>();
const connectionListeners = new Set<ConnectionListenerType>();

/** Records a connection's socket state and tells the listeners, once per change (RTC-3). */
export function setRealtimeConnectionState(
  connection: RealtimeConnectionKeyType,
  connected: boolean,
): void {
  if ((connectedByKey.get(connection) ?? false) === connected) return;
  connectedByKey.set(connection, connected);
  Array.from(connectionListeners).forEach((listener) => listener(connected, connection));
}

/**
 * Notifies when ONE connection's socket connects or drops (the main connection by default). A drop
 * means that connection's events were missed, so a listener re-queries its own work areas on the
 * way back up. Another connection's change is not heard here.
 */
export function onRealtimeConnectionChange(
  listener: (connected: boolean) => void,
  connection: RealtimeConnectionKeyType = MAIN_REALTIME_CONNECTION,
): () => void {
  return onAnyRealtimeConnectionChange((connected, changed) => {
    if (changed === connection) listener(connected);
  });
}

/** Every connection's changes, each naming the connection: for a hub that holds work areas of several. */
export function onAnyRealtimeConnectionChange(listener: ConnectionListenerType): () => void {
  connectionListeners.add(listener);
  return () => {
    connectionListeners.delete(listener);
  };
}

/**
 * I-2199: the server closes the tab's socket with 4430 when its credential's KIND may not hold one (a
 * view as another role, F-0709 ASIF-29). Only that code is final: a handshake refused for anything else
 * (a token that lapsed while the laptop slept) is a plain 4403, which `graphql-ws` retries with the
 * refreshed token. A kind refusal is final for the credential: no socket is opened again until the
 * credential changes (the view ends, sign-out), and the views fall back to their own poll.
 */
export const REALTIME_REFUSED_CLOSE_CODE = SOCKET_CREDENTIAL_KIND_REFUSED_CLOSE_CODE;

let realtimeRefused = false;
const refusedListeners = new Set<() => void>();

export function isRealtimeRefused(): boolean {
  return realtimeRefused;
}

export function setRealtimeRefused(refused: boolean): void {
  if (realtimeRefused === refused) return;
  realtimeRefused = refused;
  Array.from(refusedListeners).forEach((listener) => listener());
}

export function onRealtimeRefusedChange(listener: () => void): () => void {
  refusedListeners.add(listener);
  return () => {
    refusedListeners.delete(listener);
  };
}

/** Whether `connection`'s socket is up. The main connection is what the app's own hint reads. */
export function isRealtimeConnected(
  connection: RealtimeConnectionKeyType = MAIN_REALTIME_CONNECTION,
): boolean {
  return connectedByKey.get(connection) ?? false;
}

/**
 * A custom connection's subscriptions, through the desktop relay. One per connection: the relay
 * keeps one socket for it, and every subscription here names the binding the relay reads the
 * connection's address and credential from.
 */
class DesktopRealtimeClient implements IRealtimeSubscriber {
  constructor(
    private readonly connection: RealtimeConnectionKeyType,
    private readonly desktopWorkAreaId: string,
    private readonly open: typeof subscribeDesktopCloudRealtime = subscribeDesktopCloudRealtime,
  ) {}

  subscribe<T>(
    payload: { query: string; variables?: Record<string, unknown> | null },
    sink: Sink<FormattedExecutionResult<T>>,
  ): () => void {
    let stop: (() => void) | null = null;
    let disposed = false;
    const onMessage = (message: DesktopRealtimeMessageType) => {
      // A frame already in flight when the subscriber left is not its to hear.
      if (disposed) return;
      if (message.kind === 'state') {
        setRealtimeConnectionState(this.connection, message.state === 'connected');
        return;
      }
      if (message.kind === 'next') {
        sink.next(message.payload as FormattedExecutionResult<T>);
        return;
      }
      if (message.kind === 'error') {
        sink.error(new Error(message.message));
        return;
      }
      sink.complete();
    };
    this.open(
      {
        desktopWorkAreaId: this.desktopWorkAreaId,
        query: payload.query,
        variables: payload.variables ?? {},
      },
      onMessage,
    )
      .then((unsubscribe) => {
        if (disposed) void unsubscribe();
        else stop = () => void unsubscribe();
      })
      .catch((error: unknown) => {
        setRealtimeConnectionState(this.connection, false);
        sink.error(error instanceof Error ? error : new Error(String(error)));
      });
    return () => {
      disposed = true;
      stop?.();
    };
  }
}

const desktopClients = new Map<RealtimeConnectionKeyType, IRealtimeSubscriber>();

let relayAvailable: boolean | null = null;
let relayAnswered = false;
const relayAnswerListeners = new Set<() => void>();

function settleRelayAvailability(available: boolean): void {
  relayAvailable = available;
  relayAnswered = true;
  const waiting = Array.from(relayAnswerListeners);
  relayAnswerListeners.clear();
  waiting.forEach((listener) => listener());
}

/**
 * Whether this desktop build relays a custom connection's live updates. The web is served by a
 * deployment and may be newer than the app, so it asks once; until the answer comes, and on a build
 * that says no, a custom connection's views keep their own poll as their delivery. A caller that
 * asked before the answer came listens with `whenDesktopRealtimeAnswered` and asks again.
 */
export function desktopRealtimeAvailable(): boolean {
  if (relayAvailable === null) {
    relayAvailable = false;
    getDesktopCapabilities()
      .then((capabilities) => settleRelayAvailability(capabilities.supportsCloudRealtime === true))
      .catch(() => settleRelayAvailability(false));
  }
  return relayAvailable;
}

/** True until the relay-capability question has been answered, either way. */
export function desktopRealtimeAnswerPending(): boolean {
  return !relayAnswered;
}

/** Calls `listener` once when the relay-capability answer arrives; the returned function cancels. */
export function whenDesktopRealtimeAnswered(listener: () => void): () => void {
  relayAnswerListeners.add(listener);
  return () => {
    relayAnswerListeners.delete(listener);
  };
}

// Asked at the first use rather than at import: runtime detection at module evaluation breaks any
// importer whose mocks are not yet initialised (gate 632). The answer is asynchronous, so a caller
// that asks before it arrives must listen for it (`whenDesktopRealtimeAnswered`), not decide once.
function askRelayOnDesktop(): void {
  if (relayAvailable === null && isDesktopRuntime()) desktopRealtimeAvailable();
}

/** The realtime connection that holds `workAreaId`, and its key for state. */
export function realtimeConnectionOf(workAreaId: string | null | undefined): {
  connection: RealtimeConnectionKeyType;
  desktopWorkAreaId: string | null;
} {
  askRelayOnDesktop();
  const binding = workAreaId
    ? workAreaDataSourceRegistry.descriptor(workAreaId)?.desktopBinding
    : null;
  if (!binding?.desktopWorkAreaId) {
    return { connection: MAIN_REALTIME_CONNECTION, desktopWorkAreaId: null };
  }
  return {
    // A binding names its connection (ADR-153); one with none is reached through itself alone.
    connection: `connection:${binding.connectionId ?? binding.desktopWorkAreaId}`,
    desktopWorkAreaId: binding.desktopWorkAreaId,
  };
}

/**
 * The desktop relay's client for the connection that holds `workAreaId` (RTC-1), one per connection
 * (RTC-2); null for a work area on the main connection, whose subscriptions use the tab's socket.
 */
export function desktopRealtimeClientFor(
  workAreaId: string | null | undefined,
): IRealtimeSubscriber | null {
  const { connection, desktopWorkAreaId } = realtimeConnectionOf(workAreaId);
  if (!desktopWorkAreaId) return null;
  let client = desktopClients.get(connection);
  if (!client) {
    client = new DesktopRealtimeClient(connection, desktopWorkAreaId);
    desktopClients.set(connection, client);
  }
  return client;
}

/** Test seam: forget every per-connection client and state. */
export function resetRealtimeClientsForTests(): void {
  desktopClients.clear();
  connectedByKey.clear();
  relayAvailable = null;
  relayAnswered = false;
  relayAnswerListeners.clear();
  connectionListeners.clear();
  realtimeRefused = false;
}
